import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { SupportChatButton } from '../components/SupportChatButton';
import { 
  Book, 
  Search, 
  Stethoscope, 
  Users, 
  Gift, 
  Download, 
  CreditCard,
  Settings,
  FileText,
  Truck,
  MessageSquare
} from 'lucide-react';

const UserManual = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('diagnose');

  const categories = [
    { id: 'diagnose', name: 'KI-Diagnose', icon: Stethoscope },
    { id: 'fms', name: 'Flottenmanagement', icon: Truck },
    { id: 'referral', name: 'Empfehlungsprogramm', icon: Gift },
    { id: 'obd', name: 'OBD-Agent', icon: Download },
    { id: 'account', name: 'Konto & Abonnement', icon: Settings }
  ];

  const manualContent = {
    diagnose: [
      {
        title: '1. Neue Diagnose starten',
        steps: [
          'Klicken Sie im Dashboard auf "Neue Diagnose"',
          'Geben Sie die Symptome Ihres Fahrzeugs ein (z.B. "Motor stottert beim Beschleunigen")',
          'Optional: Fügen Sie Fehlercode hinzu (z.B. P0300)',
          'Klicken Sie auf "Diagnose starten"',
          'Die KI analysiert Ihr Problem und erstellt einen detaillierten Bericht'
        ],
        tip: '💡 Je detaillierter Sie die Symptome beschreiben, desto präziser ist die Diagnose.'
      },
      {
        title: '2. Diagnosebericht verstehen',
        steps: [
          'Der Bericht enthält: Problemursache, Schweregrad und geschätzte Reparaturkosten',
          'Schritt-für-Schritt-Reparaturanleitung mit Bildern',
          'Benötigte Werkzeuge und Ersatzteile',
          'Zeitaufwand für die Reparatur',
          'Sicherheitshinweise'
        ],
        tip: '📊 Sie können bis zu 87% der Werkstattkosten sparen, indem Sie die Diagnose selbst durchführen.'
      },
      {
        title: '3. Diagnose-Historie',
        steps: [
          'Alle Ihre Diagnosen werden automatisch gespeichert',
          'Zugriff über "Historie" in der Sidebar',
          'Filtern nach Fahrzeug, Datum oder Status',
          'Berichte als PDF exportieren'
        ]
      }
    ],
    fms: [
      {
        title: '1. Fahrzeuge verwalten',
        steps: [
          'Gehen Sie zu "Flottenmanagement" → "Fahrzeuge"',
          'Klicken Sie auf "Fahrzeug hinzufügen"',
          'Geben Sie Fahrzeugdaten ein (Kennzeichen, Marke, Modell, VIN)',
          'Weisen Sie optional einen Fahrer zu',
          'Speichern Sie das Fahrzeug'
        ],
        tip: '🚗 Sie können unbegrenzt viele Fahrzeuge in Ihrem FMS-Abo verwalten.'
      },
      {
        title: '1a. Technische Fahrzeugdaten abrufen',
        steps: [
          'Um detaillierte technische Daten zu erhalten, erstellen Sie zunächst ein Fahrzeug in "Meine Fahrzeuge"',
          'Nach dem Erstellen sammelt unsere KI automatisch technische Spezifikationen',
          'Die Datensammlung kann einige Minuten dauern (normalerweise 2-5 Minuten)',
          'Klicken Sie auf "Technische Details anzeigen" im Fahrzeugprofil',
          'Sie erhalten detaillierte Informationen: Motorspezifikationen, Getriebe, Bremsanlage, Aufhängung, Elektrik und vieles mehr'
        ],
        tip: '⏱️ Bitte haben Sie etwas Geduld - die KI sammelt umfassende technische Daten aus verschiedenen Quellen. Dies geschieht nur einmal pro Fahrzeug.'
      },
      {
        title: '2. Fahrer verwalten',
        steps: [
          'Gehen Sie zu "Flottenmanagement" → "Fahrer"',
          'Klicken Sie auf "Fahrer hinzufügen"',
          'Geben Sie Name, Führerscheinnummer und Kontaktdaten ein',
          'Weisen Sie Fahrzeuge zu',
          'Der Fahrer erhält automatisch Zugriff auf zugewiesene Fahrzeuge'
        ]
      },
      {
        title: '3. Wartungspläne erstellen',
        steps: [
          'Erstellen Sie automatische Wartungserinnerungen',
          'Definieren Sie Intervalle (z.B. alle 15.000 km oder 12 Monate)',
          'Erhalten Sie E-Mail-Benachrichtigungen',
          'Verfolgen Sie Wartungshistorie'
        ]
      }
    ],
    referral: [
      {
        title: '1. Empfehlungslink erhalten',
        steps: [
          'Ihr eindeutiger Empfehlungslink wird automatisch im Dashboard angezeigt',
          'Alternativ: Gehen Sie zu "Mein Konto" → "Empfehlungen"',
          'Klicken Sie auf "Link kopieren"',
          'Teilen Sie den Link mit Freunden per E-Mail, WhatsApp oder Social Media'
        ],
        tip: '💰 Sie erhalten €10 Guthaben für jede erfolgreiche Empfehlung!'
      },
      {
        title: '2. Credits verdienen',
        steps: [
          'Ihr Freund klickt auf Ihren Empfehlungslink',
          'Er registriert sich und schließt ein Abonnement ab',
          'Sie erhalten automatisch €10 Credits auf Ihr Konto',
          'Credits werden sofort nach der ersten Zahlung gutgeschrieben'
        ],
        tip: '🎁 Credits verfallen nie und können nicht ausgezahlt werden.'
      },
      {
        title: '3. Credits einlösen',
        steps: [
          'Credits können bei der Verlängerung Ihres Abonnements verwendet werden',
          'Beim Checkout wird Ihr verfügbares Guthaben angezeigt',
          'Wählen Sie, wie viel Guthaben Sie einlösen möchten',
          'Der Betrag wird vom Rechnungsbetrag abgezogen'
        ]
      },
      {
        title: '4. Statistiken verfolgen',
        steps: [
          'Sehen Sie, wie viele Personen auf Ihren Link geklickt haben',
          'Verfolgen Sie erfolgreiche Registrierungen',
          'Überprüfen Sie Ihr verfügbares und ausstehendes Guthaben',
          'Alle Transaktionen werden in Ihrer Credit-Historie angezeigt'
        ]
      }
    ],
    obd: [
      {
        title: '1. OBD-Agent herunterladen',
        steps: [
          'Gehen Sie zu "Downloads" in der Sidebar',
          'Wählen Sie Ihr Betriebssystem (Windows, macOS, Linux oder Android)',
          'Laden Sie die passende Version herunter',
          'Installieren Sie den Agent auf Ihrem Gerät'
        ],
        tip: '💻 Für Windows ist keine Installation erforderlich - einfach entpacken und starten!'
      },
      {
        title: '2. OBD-Dongle verbinden',
        steps: [
          'Stecken Sie den OBD-Dongle in die OBD-II-Buchse Ihres Fahrzeugs',
          'Die Buchse befindet sich meist unter dem Lenkrad',
          'Schalten Sie die Zündung ein (Motor kann aus bleiben)',
          'Der Dongle sollte aufleuchten'
        ]
      },
      {
        title: '3. Agent mit Fahrzeug verbinden',
        steps: [
          'Starten Sie den OBD-Agent',
          'Klicken Sie auf "Verbinden"',
          'Wählen Sie Ihren OBD-Dongle aus der Liste',
          'Die Verbindung wird automatisch hergestellt',
          'Live-Daten werden nun angezeigt'
        ]
      },
      {
        title: '4. Live-Diagnose durchführen',
        steps: [
          'Nach erfolgreicher Verbindung sehen Sie Live-Daten',
          'Klicken Sie auf "Fehlercode auslesen"',
          'Fehler werden automatisch zur KI-Analyse gesendet',
          'Sie erhalten sofort einen detaillierten Diagnosebericht'
        ]
      }
    ],
    account: [
      {
        title: '1. Abonnement verwalten',
        steps: [
          'Gehen Sie zu "Mein Konto" → "Abonnement"',
          'Sehen Sie Details zu Ihrem aktuellen Plan',
          'Upgrade/Downgrade Ihres Plans jederzeit möglich',
          'Verwalten Sie Zahlungsmethoden',
          'Rechnungen herunterladen'
        ]
      },
      {
        title: '2. Profil bearbeiten',
        steps: [
          'Gehen Sie zu "Mein Konto" → "Profil"',
          'Aktualisieren Sie Name, E-Mail, Telefonnummer',
          'Ändern Sie Ihr Passwort',
          'Aktualisieren Sie Rechnungsadresse'
        ]
      },
      {
        title: '3. Abonnement kündigen',
        steps: [
          'Gehen Sie zu "Mein Konto" → "Abonnement"',
          'Klicken Sie auf "Abonnement kündigen"',
          'Ihr Zugang bleibt bis zum Ende der Laufzeit aktiv',
          'Keine automatische Verlängerung',
          'Sie können jederzeit wieder aktivieren'
        ],
        tip: '⚠️ Ihr Guthaben verfällt nicht bei Kündigung.'
      }
    ]
  };

  const filteredContent = searchTerm
    ? Object.entries(manualContent).reduce((acc, [key, sections]) => {
        const filtered = sections.filter(
          section =>
            section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            section.steps.some(step => step.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        if (filtered.length > 0) acc[key] = filtered;
        return acc;
      }, {})
    : { [activeCategory]: manualContent[activeCategory] };

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Book style={{ color: '#7CD4FD' }} size={32} />
              <h1 className="text-4xl font-bold" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
                Benutzerhandbuch
              </h1>
            </div>
            <button
              onClick={() => document.querySelector('[data-support-chat-trigger]')?.click()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all hover:scale-105"
              style={{ backgroundColor: '#1EA0E6', color: '#FFFFFF' }}
            >
              <MessageSquare size={20} />
              KI-Chat starten
            </button>
          </div>
          <p className="text-lg" style={{ color: '#94A3B8' }}>
            Schritt-für-Schritt-Anleitungen für alle Funktionen von CarCortex-AI
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <Search 
              className="absolute left-3 top-1/2 transform -translate-y-1/2" 
              style={{ color: '#94A3B8' }} 
              size={20} 
            />
            <input
              type="text"
              placeholder="Suchen Sie nach Themen, Funktionen oder Schritten..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-lg text-base"
              style={{
                backgroundColor: '#0F141A',
                border: '1px solid #1F2937',
                color: '#E6EDF3',
                outline: 'none'
              }}
            />
          </div>
        </div>

        {/* Category Tabs */}
        {!searchTerm && (
          <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all"
                  style={{
                    backgroundColor: isActive ? '#7CD4FD' : '#1F2937',
                    color: isActive ? '#0B0F14' : '#94A3B8',
                    fontWeight: isActive ? '600' : '400'
                  }}
                >
                  <Icon size={18} />
                  {cat.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Content */}
        <div className="space-y-6">
          {Object.entries(filteredContent).map(([catId, sections]) => (
            <div key={catId}>
              {searchTerm && (
                <h2 className="text-2xl font-bold mb-4" style={{ color: '#E6EDF3' }}>
                  {categories.find(c => c.id === catId)?.name}
                </h2>
              )}
              {sections.map((section, idx) => (
                <Card key={idx} style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', marginBottom: '1.5rem' }}>
                  <CardHeader>
                    <CardTitle style={{ color: '#E6EDF3', fontSize: '1.5rem' }}>
                      {section.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ol className="space-y-3">
                      {section.steps.map((step, stepIdx) => (
                        <li key={stepIdx} className="flex gap-3">
                          <span 
                            className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold"
                            style={{ backgroundColor: '#7CD4FD', color: '#0B0F14' }}
                          >
                            {stepIdx + 1}
                          </span>
                          <span style={{ color: '#E6EDF3', lineHeight: '1.5' }}>
                            {step}
                          </span>
                        </li>
                      ))}
                    </ol>
                    {section.tip && (
                      <div 
                        className="p-4 rounded-lg mt-4"
                        style={{ backgroundColor: '#1F2937', borderLeft: '4px solid #7CD4FD' }}
                      >
                        <p style={{ color: '#E6EDF3', fontSize: '0.95rem' }}>
                          {section.tip}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ))}
        </div>

        {/* No Results */}
        {searchTerm && Object.keys(filteredContent).length === 0 && (
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="text-center py-12">
              <p style={{ color: '#94A3B8', fontSize: '1.1rem' }}>
                Keine Ergebnisse für &ldquo;{searchTerm}&rdquo; gefunden.
              </p>
              <p style={{ color: '#94A3B8', fontSize: '0.95rem', marginTop: '0.5rem' }}>
                Versuchen Sie andere Suchbegriffe oder durchsuchen Sie die Kategorien.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* KI-Chat Button */}
      <SupportChatButton contextType="manual" />
    </div>
  );
};

export default UserManual;
